import { z } from 'zod'

// ─── Enums / Unions ─────────────────────────────────────────────────────────

export type TriggerType =
  | 'client_created'
  | 'category_reached'
  | 'birthday_upcoming'
  | 'recurring_schedule'
  | 'purchase_completed'

export type ActionType =
  | 'coupon'
  | 'physical_gift_voucher'
  | 'free_shipping_flag'
  | 'email_notification'
  | 'points_bonus'

export type BenefitStatus = 'active' | 'used' | 'expired' | 'cancelled'
export type VoucherStatus = 'pending' | 'redeemed' | 'expired' | 'cancelled'

export type RecurrenceType =
  | 'first_monday'
  | 'first_tuesday'
  | 'first_wednesday'
  | 'first_thursday'
  | 'first_friday'
  | 'first_saturday'
  | 'first_sunday'
  | 'first_day'
  | 'last_day'
  | '15th'

// ─── API Response Interfaces ────────────────────────────────────────────────

export interface BenefitRule {
  id: number
  id_company: number
  name: string
  description: string | null
  internal_notes: string | null
  is_active: boolean
  trigger_type: TriggerType
  trigger_config: Record<string, unknown>
  action_type: ActionType
  action_config: Record<string, unknown>
  rules_config: Record<string, unknown>
  priority: number
  created_at: string
  updated_at: string
}

export interface ClientBenefit {
  id: number
  rule_name: string
  rule_description: string | null
  action_type: ActionType
  status: BenefitStatus
  granted_at: string
  expires_at: string | null
  used_at: string | null
  metadata: Record<string, unknown>
}

export interface ClientBenefitsResponse {
  client_id: number
  category: string
  active_benefits: ClientBenefit[]
  used_benefits_count: number
  expired_benefits_count: number
}

export interface BenefitVoucher {
  id: number
  voucher_code: string
  voucher_type: string
  gift_description: string | null
  status: VoucherStatus
  valid_from: string
  valid_until: string
  redeemed_at: string | null
  redeemed_by: { id: number; name: string } | null
  redeemed_at_branch: { id: number; address: string } | null
  redeemed_product: string | null
  redeemed_amount: number | null
  client: {
    id: number
    name: string
    last_name: string
    rut: string
  }
}

// Extended benefit item (from /all and /history endpoints, includes cancelled_at)
export interface ClientBenefitHistoryItem extends ClientBenefit {
  cancelled_at: string | null
}

// ─── Available Benefits (catalog by tier) ───────────────────────────────────

export interface AvailableBenefitItem {
  rule_id: number
  name: string
  description: string | null
  action_type: ActionType
  icon_hint: string | null
}

export interface AvailableBenefitTier {
  category: string
  category_display_name: string
  threshold: number
  benefits: AvailableBenefitItem[]
}

export interface AvailableBenefitsResponse {
  tiers: AvailableBenefitTier[]
}

// ─── Stats ──────────────────────────────────────────────────────────────────

export interface BenefitRuleStats {
  rule_id: number
  rule_name: string
  granted: number
  used: number
}

export interface BenefitStats {
  active_this_month: number
  used_this_month: number
  expired_this_month: number
  pending_vouchers: number
  rules_active: number
  by_rule: BenefitRuleStats[]
}

export interface VoucherRedeemPayload {
  redeemed_by_user_id: number
  redeemed_at_branch_id: number
  redeemed_product: string
  redeemed_amount: number
}

// ─── Filters ────────────────────────────────────────────────────────────────

export interface BenefitRuleFilters {
  page: number
  limit: number
  is_active?: boolean
  trigger_type?: TriggerType
  action_type?: ActionType
}

export interface VoucherFilters {
  page: number
  limit: number
  status?: VoucherStatus
  voucher_type?: string
  search?: string
}

// ─── Paginated Response ─────────────────────────────────────────────────────

export interface BenefitRulePaginatedResponse {
  data: BenefitRule[]
  total_items: number
  current_page: number
  total_pages: number
  current_items_count: number
}

export interface VoucherPaginatedResponse {
  data: BenefitVoucher[]
  total_items: number
  current_page: number
  total_pages: number
  current_items_count: number
}

// ─── Zod Schemas (3 steps) ──────────────────────────────────────────────────

// Labels for UI display
export const TRIGGER_TYPE_LABELS: Record<TriggerType, string> = {
  client_created: 'Cliente creado',
  category_reached: 'Categoría alcanzada',
  birthday_upcoming: 'Cumpleaños próximo',
  recurring_schedule: 'Programación recurrente',
  purchase_completed: 'Compra completada',
}

export const ACTION_TYPE_LABELS: Record<ActionType, string> = {
  coupon: 'Cupón de descuento',
  physical_gift_voucher: 'Vale de regalo físico',
  free_shipping_flag: 'Despacho gratis',
  email_notification: 'Notificación por email',
  points_bonus: 'Bonus de puntos',
}

export const TRIGGER_TYPE_DESCRIPTIONS: Record<TriggerType, string> = {
  client_created: 'Se activa cuando un cliente nuevo es registrado',
  category_reached: 'Se activa cuando el cliente alcanza una categoría',
  birthday_upcoming: 'Se activa días antes del cumpleaños del cliente',
  recurring_schedule: 'Se ejecuta en un horario recurrente configurado',
  purchase_completed: 'Se activa al completar una compra',
}

export const ACTION_TYPE_DESCRIPTIONS: Record<ActionType, string> = {
  coupon: 'Genera un cupón de descuento canjeable en web o tienda',
  physical_gift_voucher: 'Genera un vale canjeable en tienda física',
  free_shipping_flag: 'Otorga despacho gratis en la próxima compra',
  email_notification: 'Envía un email personalizado al cliente',
  points_bonus: 'Otorga puntos adicionales al cliente',
}

export const RECURRENCE_LABELS: Record<RecurrenceType, string> = {
  first_monday: 'Primer lunes del mes',
  first_tuesday: 'Primer martes del mes',
  first_wednesday: 'Primer miércoles del mes',
  first_thursday: 'Primer jueves del mes',
  first_friday: 'Primer viernes del mes',
  first_saturday: 'Primer sábado del mes',
  first_sunday: 'Primer domingo del mes',
  first_day: 'Primer día del mes',
  last_day: 'Último día del mes',
  '15th': 'Día 15 del mes',
}

export const CATEGORY_OPTIONS = ['bronce', 'marmol', 'oro'] as const

// Step 1: Trigger
export const stepTriggerSchema = z.object({
  name: z.string().min(1, 'Nombre es requerido'),
  description: z.string().optional().default(''),
  internal_notes: z.string().optional().default(''),
  trigger_type: z.enum([
    'client_created',
    'category_reached',
    'birthday_upcoming',
    'recurring_schedule',
    'purchase_completed',
  ], { required_error: 'Selecciona un tipo de gatillo' }),
  // Dynamic trigger_config fields (all optional, validated contextually)
  trigger_days_before: z.coerce.number().min(1).optional(),
  trigger_category: z.string().optional(),
  trigger_recurrence: z.string().optional(),
  trigger_timezone: z.string().optional(),
  trigger_channel: z.string().optional(),
})

export type StepTriggerData = z.infer<typeof stepTriggerSchema>

// Step 2: Action
export const stepActionSchema = z.object({
  action_type: z.enum([
    'coupon',
    'physical_gift_voucher',
    'free_shipping_flag',
    'email_notification',
    'points_bonus',
  ], { required_error: 'Selecciona un tipo de acción' }),
  // Coupon fields (unified)
  coupon_discount_type: z.enum(['percent', 'fixed_cart']).optional(),
  coupon_amount: z.coerce.number().min(0).optional(),
  coupon_max_discount: z.coerce.number().min(0).optional(),
  coupon_code_prefix: z.string().optional(),
  // Physical gift voucher fields
  gift_description: z.string().optional(),
  gift_valid_days: z.coerce.number().min(1).optional(),
  // Free shipping fields
  shipping_condition: z.enum(['first_purchase', 'min_amount', 'permanent']).optional(),
  shipping_min_amount: z.coerce.number().min(0).optional(),
  // Points bonus fields
  bonus_points: z.coerce.number().min(1).optional(),
  // Email notification fields
  email_template: z.string().optional(),
  email_subject: z.string().optional(),
})

export type StepActionData = z.infer<typeof stepActionSchema>

// Helper: coerce to number but treat empty strings / NaN as undefined
const optionalNumber = z.preprocess(
  (val) => {
    if (val === '' || val === undefined || val === null) return undefined
    const n = Number(val)
    return Number.isNaN(n) ? undefined : n
  },
  z.number().min(0).optional(),
)

const optionalPositiveNumber = z.preprocess(
  (val) => {
    if (val === '' || val === undefined || val === null) return undefined
    const n = Number(val)
    return Number.isNaN(n) ? undefined : n
  },
  z.number().min(1).optional(),
)

// Step 3: Rules / Limitations
export const stepRulesSchema = z.object({
  eligible_categories: z.array(z.string()).min(1, 'Selecciona al menos una categoría'),
  single_use: z.boolean().default(false),
  valid_days: optionalPositiveNumber,
  valid_days_unlimited: z.boolean().default(false),
  max_grants_per_year: optionalPositiveNumber,
  max_grants_per_year_unlimited: z.boolean().default(false),
  max_grants_per_month: optionalPositiveNumber,
  max_grants_per_month_unlimited: z.boolean().default(false),
  min_purchase: optionalNumber,
  min_purchase_none: z.boolean().default(true),
  max_discount: optionalNumber,
  max_discount_none: z.boolean().default(true),
  valid_channels: z.array(z.string()).min(1, 'Selecciona al menos un canal'),
  non_combinable: z.boolean().default(false),
  requires_email: z.boolean().default(false),
  cooldown_days: optionalPositiveNumber,
  cooldown_days_none: z.boolean().default(true),
  valid_until_first_purchase: z.boolean().default(false),
  priority: z.coerce.number().min(0).default(10),
})

export type StepRulesData = z.infer<typeof stepRulesSchema>

// ─── Helpers to build API payload from wizard data ──────────────────────────

export function buildTriggerConfig(data: StepTriggerData): Record<string, unknown> {
  switch (data.trigger_type) {
    case 'birthday_upcoming':
      return { days_before: data.trigger_days_before ?? 7 }
    case 'category_reached':
      return { category: data.trigger_category ?? 'bronce' }
    case 'recurring_schedule':
      return {
        recurrence: data.trigger_recurrence ?? 'first_monday',
        timezone: data.trigger_timezone ?? 'America/Santiago',
      }
    case 'purchase_completed':
      return { channel: data.trigger_channel ?? 'all' }
    case 'client_created':
    default:
      return {}
  }
}

export function buildActionConfig(data: StepActionData): Record<string, unknown> {
  switch (data.action_type) {
    case 'coupon':
      return {
        discount_type: data.coupon_discount_type ?? 'percent',
        amount: data.coupon_amount ?? 0,
        max_discount: data.coupon_max_discount ?? 0,
        code_prefix: data.coupon_code_prefix ?? 'CLUB',
      }
    case 'physical_gift_voucher':
      return {
        gift_description: data.gift_description ?? '',
        valid_days: data.gift_valid_days ?? 30,
      }
    case 'free_shipping_flag':
      return {
        condition: data.shipping_condition ?? 'first_purchase',
        ...(data.shipping_condition === 'min_amount' && { min_amount: data.shipping_min_amount ?? 0 }),
      }
    case 'points_bonus':
      return { bonus_points: data.bonus_points ?? 0 }
    case 'email_notification':
      return {
        template: data.email_template ?? '',
        subject: data.email_subject ?? '',
      }
    default:
      return {}
  }
}

export function buildRulesConfig(data: StepRulesData): Record<string, unknown> {
  return {
    eligible_categories: data.eligible_categories,
    single_use: data.single_use,
    ...(data.valid_days_unlimited ? {} : { valid_days: data.valid_days }),
    ...(data.max_grants_per_year_unlimited ? {} : { max_grants_per_year: data.max_grants_per_year }),
    ...(data.max_grants_per_month_unlimited ? {} : { max_grants_per_month: data.max_grants_per_month }),
    ...(data.min_purchase_none ? {} : { min_purchase: data.min_purchase }),
    ...(data.max_discount_none ? {} : { max_discount: data.max_discount }),
    valid_channels: data.valid_channels,
    non_combinable: data.non_combinable,
    requires_email: data.requires_email,
    ...(data.cooldown_days_none ? {} : { cooldown_days: data.cooldown_days }),
    ...(data.valid_until_first_purchase ? { valid_until_first_purchase: true } : {}),
  }
}

// Build the full API payload from the 3 wizard steps
export function buildBenefitRulePayload(
  trigger: StepTriggerData,
  action: StepActionData,
  rules: StepRulesData,
) {
  return {
    name: trigger.name,
    description: trigger.description || null,
    internal_notes: trigger.internal_notes || null,
    is_active: true,
    trigger_type: trigger.trigger_type,
    trigger_config: buildTriggerConfig(trigger),
    action_type: action.action_type,
    action_config: buildActionConfig(action),
    rules_config: buildRulesConfig(rules),
    priority: rules.priority,
  }
}

// ─── Helpers to extract wizard data from API response (for edit mode) ───────

export function extractTriggerData(rule: BenefitRule): Partial<StepTriggerData> {
  const base = {
    name: rule.name,
    description: rule.description ?? '',
    internal_notes: rule.internal_notes ?? '',
    trigger_type: rule.trigger_type,
  }
  const cfg = rule.trigger_config
  switch (rule.trigger_type) {
    case 'birthday_upcoming':
      return { ...base, trigger_days_before: (cfg.days_before as number) ?? 7 }
    case 'category_reached':
      return { ...base, trigger_category: (cfg.category as string) ?? 'bronce' }
    case 'recurring_schedule':
      return {
        ...base,
        trigger_recurrence: (cfg.recurrence as string) ?? 'first_monday',
        trigger_timezone: (cfg.timezone as string) ?? 'America/Santiago',
      }
    case 'purchase_completed':
      return { ...base, trigger_channel: (cfg.channel as string) ?? 'all' }
    default:
      return base
  }
}

export function extractActionData(rule: BenefitRule): Partial<StepActionData> {
  const base = { action_type: rule.action_type }
  const cfg = rule.action_config
  switch (rule.action_type) {
    case 'coupon':
      return {
        ...base,
        coupon_discount_type: (cfg.discount_type as 'percent' | 'fixed_cart') ?? 'percent',
        coupon_amount: (cfg.amount as number) ?? 0,
        coupon_max_discount: (cfg.max_discount as number) ?? 0,
        coupon_code_prefix: (cfg.code_prefix as string) ?? 'CLUB',
      }
    case 'physical_gift_voucher':
      return {
        ...base,
        gift_description: (cfg.gift_description as string) ?? '',
        gift_valid_days: (cfg.valid_days as number) ?? 30,
      }
    case 'free_shipping_flag':
      return {
        ...base,
        shipping_condition: (cfg.condition as 'first_purchase' | 'min_amount' | 'permanent') ?? 'first_purchase',
        shipping_min_amount: (cfg.min_amount as number) ?? 0,
      }
    case 'points_bonus':
      return { ...base, bonus_points: (cfg.bonus_points as number) ?? 0 }
    case 'email_notification':
      return {
        ...base,
        email_template: (cfg.template as string) ?? '',
        email_subject: (cfg.subject as string) ?? '',
      }
    default:
      return base
  }
}

export function extractRulesData(rule: BenefitRule): Partial<StepRulesData> {
  const cfg = rule.rules_config
  const hasValidDays = cfg.valid_days !== undefined && cfg.valid_days !== null
  const hasMaxYear = cfg.max_grants_per_year !== undefined && cfg.max_grants_per_year !== null
  const hasMaxMonth = cfg.max_grants_per_month !== undefined && cfg.max_grants_per_month !== null
  const hasMinPurchase = cfg.min_purchase !== undefined && cfg.min_purchase !== null
  const hasMaxDiscount = cfg.max_discount !== undefined && cfg.max_discount !== null
  const hasCooldownDays = cfg.cooldown_days !== undefined && cfg.cooldown_days !== null

  return {
    eligible_categories: (cfg.eligible_categories as string[]) ?? ['bronce'],
    single_use: (cfg.single_use as boolean) ?? false,
    valid_days: hasValidDays ? (cfg.valid_days as number) : undefined,
    valid_days_unlimited: !hasValidDays,
    max_grants_per_year: hasMaxYear ? (cfg.max_grants_per_year as number) : undefined,
    max_grants_per_year_unlimited: !hasMaxYear,
    max_grants_per_month: hasMaxMonth ? (cfg.max_grants_per_month as number) : undefined,
    max_grants_per_month_unlimited: !hasMaxMonth,
    min_purchase: hasMinPurchase ? (cfg.min_purchase as number) : undefined,
    min_purchase_none: !hasMinPurchase,
    max_discount: hasMaxDiscount ? (cfg.max_discount as number) : undefined,
    max_discount_none: !hasMaxDiscount,
    valid_channels: (cfg.valid_channels as string[]) ?? ['web'],
    non_combinable: (cfg.non_combinable as boolean) ?? false,
    requires_email: (cfg.requires_email as boolean) ?? false,
    cooldown_days: hasCooldownDays ? (cfg.cooldown_days as number) : undefined,
    cooldown_days_none: !hasCooldownDays,
    valid_until_first_purchase: (cfg.valid_until_first_purchase as boolean) ?? false,
    priority: rule.priority,
  }
}


// ============================================================================
// Backfill (Retroactive benefit assignment)
// ============================================================================

export interface BackfillPreviewRequest {
  category: string
  rule_ids: number[]
}

export interface BackfillPreviewRuleSkipReasons {
  single_use: number
  max_grants_per_year: number
  max_grants_per_month: number
  cooldown_days: number
  requires_email: number
  non_combinable: number
  valid_until_first_purchase: number
  already_has_active_benefit: number
  other: number
}

export interface BackfillPreviewRuleDetail {
  rule_id: number
  rule_name: string
  action_type: ActionType
  eligible_count: number
  skipped_count: number
  skip_reasons: BackfillPreviewRuleSkipReasons
}

export interface BackfillPreviewClientItem {
  id: number
  name: string
  rut: string
  email: string | null
  eligible_rule_ids: number[]
}

export interface BackfillPreviewResponse {
  category: string
  total_clients: number
  eligible_clients: number
  skipped_clients: number
  rules: BackfillPreviewRuleDetail[]
  clients: BackfillPreviewClientItem[]
}

export interface BackfillGrantRequest {
  client_id: number
  rule_ids: number[]
  backfill_reason: string
}

export interface BackfillGrantResultItem {
  rule_id: number
  rule_name: string
  status: 'granted' | 'skipped' | 'error'
  client_benefit_id?: number
  reason?: string
}

export interface BackfillGrantResponse {
  client_id: number
  results: BackfillGrantResultItem[]
}

export interface BackfillExecutionLog {
  clientId: number
  clientName: string
  clientRut: string
  results: BackfillGrantResultItem[]
  timestamp: string
}

export interface BackfillSummary {
  totalProcessed: number
  totalGranted: number
  totalSkipped: number
  totalErrors: number
  logs: BackfillExecutionLog[]
}

export const SKIP_REASON_LABELS: Record<string, string> = {
  single_use: 'Uso único (ya otorgado)',
  max_grants_per_year: 'Límite anual alcanzado',
  max_grants_per_month: 'Límite mensual alcanzado',
  cooldown_days: 'En período de enfriamiento',
  requires_email: 'Sin email',
  non_combinable: 'No acumulable',
  valid_until_first_purchase: 'Ya tiene beneficio activo',
  already_has_active_benefit: 'Ya tiene este beneficio activo',
  eligible_categories: 'Categoría no elegible',
  other: 'Otra razón',
}

export const CATEGORY_DISPLAY_NAMES: Record<string, string> = {
  bronce: 'Bronce',
  marmol: 'Mármol',
  oro: 'Oro',
}

export const CATEGORY_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  bronce: { bg: 'bg-orange-50', text: 'text-orange-800', border: 'border-orange-200' },
  marmol: { bg: 'bg-gray-50', text: 'text-gray-800', border: 'border-gray-300' },
  oro: { bg: 'bg-yellow-50', text: 'text-yellow-800', border: 'border-yellow-300' },
}
